import { c as _c } from "react/compiler-runtime";
import { memo, useRef, useEffect, useLayoutEffect } from "react";
import { AgentTextMessage, UserTextMessage, NavigateMessage,
// ThinkingIndicator,
AgentButtonMessage, DynamicFormMessage, SelectMessage, SystemMessage, DebugMessage } from "../messages";
import type { Message } from "@/types/agent/message";
import type { AgentStatusStep } from "@/features/agent";
import { getTranslations, type Locale } from "@/lib/i18n/config";
interface ChatHistoryProps {
  messages: Message[];
  debug?: boolean;
  statusStep?: AgentStatusStep | null;
  locale?: Locale;
  /** True while a voice session is running. */
  isVoiceActive?: boolean;
}

/**
 * Memoized per message. `parseMarkdown` re-parses a message in full on every
 * render, so without this every controller state change re-parsed the entire
 * conversation — quadratic once token-wise streaming updates the last bubble per
 * token. `updateMessage` clones only the message it touches, so identity is
 * stable for all the others and they bail out here.
 */
const MessageRenderer = memo(function MessageRenderer(t0) {
  const $ = _c(15);
  const {
    message,
    locale
  } = t0;
  if (message.sender === "user" && message.type === "text") {
    let t1;
    if ($[0] !== message) {
      t1 = <UserTextMessage message={message} />;
      $[0] = message;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  switch (message.type) {
    case "system":
      {
        let t1;
        if ($[2] !== message) {
          t1 = <SystemMessage message={message} />;
          $[2] = message;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "text":
      {
        let t1;
        if ($[4] !== message) {
          t1 = <AgentTextMessage message={message} />;
          $[4] = message;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "navigate":
      {
        let t1;
        if ($[6] !== message) {
          t1 = <NavigateMessage message={message} />;
          $[6] = message;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case "button":
      {
        let t1;
        if ($[8] !== message) {
          t1 = <AgentButtonMessage message={message} />;
          $[8] = message;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        return t1;
      }
    case "dynamic-form":
      {
        let t1;
        if ($[10] !== locale || $[11] !== message) {
          t1 = <DynamicFormMessage message={message} locale={locale} />;
          $[10] = locale;
          $[11] = message;
          $[12] = t1;
        } else {
          t1 = $[12];
        }
        return t1;
      }
    case "select":
      {
        let t1;
        if ($[13] !== message) {
          t1 = <SelectMessage message={message} />;
          $[13] = message;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
});
export const ChatHistory: React.FC<ChatHistoryProps> = t0 => {
  const $ = _c(21);
  const {
    messages,
    debug,
    statusStep,
    locale: t1,
    isVoiceActive: t2
  } = t0;
  const locale = t1 === undefined ? "de" : t1;
  const isVoiceActive = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== locale) {
    t3 = getTranslations(locale);
    $[0] = locale;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    agent: t
  } = t3;
  const chatContainerRef = useRef(null);
  const hasMountedRef = useRef(false);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const container = chatContainerRef.current;
      if (!container) {
        return;
      }
      container.scrollTop = container.scrollHeight;
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }
      const container_0 = chatContainerRef.current;
      if (!container_0) {
        return;
      }
      container_0.scrollTo({
        top: container_0.scrollHeight,
        behavior: "smooth"
      });
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== messages || $[6] !== statusStep) {
    t7 = [messages, statusStep];
    $[5] = messages;
    $[6] = statusStep;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  useEffect(t6, t7);
  const t8 = t.chatHistoryLabel;
  const t9 = isVoiceActive ? "off" : "polite";
  let t10;
  if ($[8] !== debug || $[9] !== locale || $[10] !== messages) {
    let t11;
    if ($[12] !== debug || $[13] !== locale) {
      t11 = message => <li key={message.id} className={`max-w-full ${message.type === "system" ? "self-center" : message.sender === "assistant" ? "self-start" : "self-end"}`}><MessageRenderer message={message} locale={locale} />{debug && <DebugMessage content={JSON.stringify(message, null, 2)} />}</li>;
      $[12] = debug;
      $[13] = locale;
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    t10 = messages.map(t11);
    $[8] = debug;
    $[9] = locale;
    $[10] = messages;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[15] !== t10) {
    t11 = <ul className="flex flex-col gap-5 justify-end px-4 min-h-full">{t10}</ul>;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t.chatHistoryLabel || $[18] !== t11 || $[19] !== t9) {
    t12 = <section ref={chatContainerRef} className="h-full overflow-y-scroll [&::-webkit-scrollbar]:hidden [scrollbar-width:none] overscroll-contain p-2 font-medium mask-[linear-gradient(to_bottom,transparent,black_0.5em,black_calc(100%-0.5em),transparent)]" role="log" aria-label={t8} aria-live={t9} aria-relevant="additions">{t11}</section>;
    $[17] = t.chatHistoryLabel;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};